import React from "react";
import {
    Button,
    notification
} from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { isStaffEmail } from "@utils/user_utils";
import { useChartsManager } from "./useChartsManager";
import { AllCustomizations } from "./AllCustomizations";
import { SingleChartCustomizer } from "./SingleChartCustomizer";
import { useSelector, useDispatch } from "react-redux";
import { useUserPermissions } from "@components/PermissionedComponents";
import { getTeamInfo } from "@store/actions/team.actions";
import { PremiumFeatureDisabled } from "../../Common/PremiumFeatureDisabled";
import BlurredCustomCharts from "./custom_charts_blurred.png";
import { AdminNotice } from "../AdminNotice";

export const CustomCharts = () => {
    const { info } = useSelector(({ team }) => team);
    const dispatch = useDispatch();
    const { user } = useAuth0();
    const isStaff = isStaffEmail(user?.email);
    const userPermissions = useUserPermissions();
    const canView = userPermissions?.ViewPlatformCharts;
    const canEdit = userPermissions?.ManagePlatformCharts;
    const permissionsManagerList = info?.permissions_manager_list || [];
    const managerNames = permissionsManagerList
        .map(user => `${user.first_name} ${user.last_name}`)
        .join(", ");
    const canLoadCharts = isStaff || (info?.editable_platform_charts_enabled && canView);
    const chartManager = useChartsManager(canLoadCharts);

    const handleSaveChanges = async () => {
        // Perform final validation using finalValidation()
        const validationError = chartManager.finalValidation();
        if (validationError) {
            // If validation returns an error message, show an error modal.
            notification.error({
                message: "Error saving changes:",
                description: validationError,
                placement: 'top',
                style: {
                    width: '30rem'
                },
                duration: 10
            })
            return;
        }
        // If no errors, save the charts.
        try {
            await chartManager.saveCharts();
            dispatch(getTeamInfo());
            notification.success({
                message: "Success",
                description: "Changes saved successfully!",
                placement: 'top',
                style: {
                    width: '30rem'
                },
                duration: 5
            });
        } catch (error) {
            const message = error?.response?.data?.error
                || error?.response?.data
                || error?.message
                || "An unexpected error occurred.";
            notification.error({
                message: "Error saving changes:",
                description: typeof message === 'string' ? message : JSON.stringify(message),
                placement: 'top',
                style: {
                    width: '30rem'
                },
                duration: 10
            })
        }
    };

    if (!info?.editable_platform_charts_enabled && !isStaff) {
        return (<PremiumFeatureDisabled show_navbar={false}
                    bg_image={BlurredCustomCharts}
                    backgroundSize={"contain"}
                    feature_name={"Custom Charts"}
                    info_text={<>
                        This is an elite feature that will allow you to customize your platform charts. <br /> <br />
                        If you are interested in adding this to your platform, please contact your sales rep!</>}
                />
        );
    }

    if (!canView && !isStaff) {
        return (
            <div>
                You do not have permissions to view custom charts. <br />
                If you want to add/remove specific permissions to your account, please reach out to your team's permissions manager. <br /><br />
                Your team's Permissions Manager(s): <strong>{managerNames}</strong>
            </div>
        );
    }

    const showAdminNotice = !info?.editable_platform_charts_enabled && isStaff;

    return (<>
        {showAdminNotice && <AdminNotice />}
        {!canEdit && !isStaff && (
            <div style={{
                padding: '12px 16px',
                marginBottom: 16,
                backgroundColor: '#fff7e6',
                border: '1px solid #ffd591',
                borderRadius: 6,
            }}>
                You have view-only access to custom charts. <br />
                If you want to add/remove specific permissions to your account, please reach out to your team's permissions manager. <br /><br />
                Your team's Permissions Manager(s): <strong>{managerNames}</strong>
            </div>
        )}
        <div
            style={{
                width: "100%",
                display: "flex",
                gap: 16,
                marginBottom: 16,
                paddingTop: 16,
            }}
        >
            {(canEdit || isStaff) && (
                <Button type="primary" onClick={handleSaveChanges}>
                    Save Changes
                </Button>
            )}
            {(canEdit || isStaff) && (
                <Button
                    type="primary"
                    onClick={chartManager.handleAddChartToOverview}
                    disabled={chartManager.getAllStateCharts('overview').length >= chartManager.maxOverviewCharts}
                >
                    Add New Chart
                </Button>
            )}
        </div>

        <div style={{ display: 'flex', gap: '20px', padding: '20px', paddingLeft: '0px' }}>
            <AllCustomizations chartManager={chartManager} />
            {(canEdit || isStaff) && (
                <SingleChartCustomizer key={"chartCustomizerRefreshKey_" + chartManager?.editingChart?.index + "_" + chartManager?.editingChart?.key} chartManager={chartManager} />
            )}
        </div>
    </>
    );
};
